import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import { useContrats } from "../ContratsProvider";
import { CONTRAT_STATUT_OPTIONS, type Contrat, type ContratInput, type ContratStatut } from "../types";

interface ContratFormProps {
  open: boolean;
  onClose: () => void;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  defaultLocataireId?: string;
  initial?: Contrat;
}

interface FormState {
  immeubleId: string;
  bienId: string;
  locataireId: string;
  dateDebut: string;
  dateFin: string;
  loyerMensuel: string;
  caution: string;
  avance: string;
  jourEcheance: string;
  conditionsParticulieres: string;
  document: string;
  statut: ContratStatut;
}

const EMPTY_STATE: FormState = {
  immeubleId: "",
  bienId: "",
  locataireId: "",
  dateDebut: "",
  dateFin: "",
  loyerMensuel: "",
  caution: "",
  avance: "",
  jourEcheance: "",
  conditionsParticulieres: "",
  document: "",
  statut: "brouillon",
};

export default function ContratForm({
  open,
  onClose,
  defaultImmeubleId,
  defaultBienId,
  defaultLocataireId,
  initial,
}: ContratFormProps) {
  const { immeubles } = useImmeubles();
  const { biens, getBien } = useBiens();
  const { locataires } = useLocataires();
  const { addContrat, updateContrat } = useContrats();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        locataireId: initial.locataireId,
        dateDebut: initial.dateDebut,
        dateFin: initial.dateFin,
        loyerMensuel: String(initial.loyerMensuel),
        caution: initial.caution ? String(initial.caution) : "",
        avance: initial.avance ? String(initial.avance) : "",
        jourEcheance: initial.jourEcheance ? String(initial.jourEcheance) : "",
        conditionsParticulieres: initial.conditionsParticulieres,
        document: initial.document,
        statut: initial.statut,
      });
      setErrors({});
      return;
    }
    setForm((prev) => {
      const next = { ...prev };
      if (defaultImmeubleId) next.immeubleId = defaultImmeubleId;
      if (defaultBienId) {
        next.bienId = defaultBienId;
        const bien = getBien(defaultBienId);
        if (bien && !next.immeubleId) next.immeubleId = bien.immeubleId;
      }
      if (defaultLocataireId) next.locataireId = defaultLocataireId;
      return next;
    });
  }, [open, defaultImmeubleId, defaultBienId, defaultLocataireId, getBien]);

  const immeubleOptions = useMemo(
    () => immeubles.map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles]
  );

  const locataireOptions = useMemo(
    () => locataires.map((locataire) => ({ value: locataire.id, label: locataireNomComplet(locataire) })),
    [locataires]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "immeubleId") next.bienId = "";
      if (key === "bienId") {
        const bien = getBien(value);
        if (bien) {
          next.immeubleId = bien.immeubleId;
          if (!prev.loyerMensuel) next.loyerMensuel = String(bien.loyerMensuel);
        }
      }
      return next;
    });
  };

  const handleDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    update("document", file ? file.name : "");
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble du contrat.";
    if (!form.bienId) nextErrors.bienId = "Sélectionnez le bien concerné.";
    if (!form.locataireId) nextErrors.locataireId = "Sélectionnez le locataire.";
    if (!form.dateDebut) nextErrors.dateDebut = "Indiquez la date de début.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: ContratInput = {
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      locataireId: form.locataireId,
      dateDebut: form.dateDebut,
      dateFin: form.dateFin,
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      caution: Math.max(0, Number(form.caution) || 0),
      avance: Math.max(0, Number(form.avance) || 0),
      jourEcheance: Math.min(31, Math.max(1, Math.round(Number(form.jourEcheance) || 1))),
      conditionsParticulieres: form.conditionsParticulieres.trim(),
      document: form.document,
      statut: form.statut,
    };

    if (initial) {
      updateContrat(initial.id, input);
    } else {
      addContrat(input);
    }
    resetAndClose();
  };

  const canSubmit = immeubles.length > 0 && biens.length > 0 && locataires.length > 0;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le contrat" : "Ajouter un contrat"}
      description="Rattachez un locataire à un bien d'un immeuble. Immeuble, bien, locataire, date de début et loyer sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="contrat-form"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le contrat
          </button>
        </>
      }
    >
      <form id="contrat-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Immeuble" htmlFor="ctr-immeuble" required error={errors.immeubleId}>
            <Select
              id="ctr-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={immeubles.length > 0 ? "Sélectionnez un immeuble" : "Créez d'abord un immeuble"}
              disabled={immeubles.length === 0}
              invalid={Boolean(errors.immeubleId)}
            />
          </Field>

          <Field
            label="Bien"
            htmlFor="ctr-bien"
            required
            error={errors.bienId}
            hint="Les biens affichés correspondent à l'immeuble sélectionné."
          >
            <Select
              id="ctr-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={
                !form.immeubleId && immeubles.length > 0
                  ? "Choisissez d'abord un immeuble"
                  : biens.length > 0
                    ? "Sélectionnez un bien"
                    : "Créez d'abord un bien"
              }
              disabled={bienOptions.length === 0}
              invalid={Boolean(errors.bienId)}
            />
          </Field>

          <Field
            label="Locataire"
            htmlFor="ctr-locataire"
            required
            error={errors.locataireId}
            hint="Choisissez un locataire déjà créé dans le module Locataires."
            className="md:col-span-2"
          >
            <Select
              id="ctr-locataire"
              value={form.locataireId}
              onChange={(event) => update("locataireId", event.target.value)}
              options={locataireOptions}
              placeholder={locataires.length > 0 ? "Sélectionnez un locataire" : "Créez d'abord un locataire"}
              disabled={locataires.length === 0}
              invalid={Boolean(errors.locataireId)}
            />
          </Field>

          <Field label="Date de début" htmlFor="ctr-debut" required error={errors.dateDebut}>
            <Input
              id="ctr-debut"
              type="date"
              value={form.dateDebut}
              onChange={(event) => update("dateDebut", event.target.value)}
              invalid={Boolean(errors.dateDebut)}
            />
          </Field>

          <Field label="Date de fin" htmlFor="ctr-fin">
            <Input
              id="ctr-fin"
              type="date"
              value={form.dateFin}
              onChange={(event) => update("dateFin", event.target.value)}
            />
          </Field>

          <Field label="Loyer mensuel (FCFA)" htmlFor="ctr-loyer" required error={errors.loyerMensuel}>
            <Input
              id="ctr-loyer"
              type="number"
              min={0}
              max={100000000}
              value={form.loyerMensuel}
              onChange={(event) => update("loyerMensuel", event.target.value)}
              placeholder="Ex. 75000"
              invalid={Boolean(errors.loyerMensuel)}
            />
          </Field>

          <Field label="Jour d'échéance" htmlFor="ctr-echeance" hint="Jour du mois (1 à 31).">
            <Input
              id="ctr-echeance"
              type="number"
              min={1}
              max={31}
              step={1}
              value={form.jourEcheance}
              onChange={(event) => update("jourEcheance", event.target.value)}
              placeholder="Ex. 5"
            />
          </Field>

          <Field label="Caution (FCFA)" htmlFor="ctr-caution">
            <Input
              id="ctr-caution"
              type="number"
              min={0}
              max={100000000}
              value={form.caution}
              onChange={(event) => update("caution", event.target.value)}
              placeholder="Ex. 150000"
            />
          </Field>

          <Field label="Avance (FCFA)" htmlFor="ctr-avance">
            <Input
              id="ctr-avance"
              type="number"
              min={0}
              max={100000000}
              value={form.avance}
              onChange={(event) => update("avance", event.target.value)}
              placeholder="Ex. 75000"
            />
          </Field>

          <Field label="Statut" htmlFor="ctr-statut">
            <Select
              id="ctr-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={CONTRAT_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Document du contrat" htmlFor="ctr-document" hint="PDF ou image (le fichier reste local).">
            <Input
              id="ctr-document"
              type="file"
              accept="application/pdf,image/*"
              onChange={handleDocument}
              className="file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-background-100 file:px-3 file:py-1.5 file:text-sm file:font-label file:text-foreground-800"
            />
          </Field>

          <Field label="Conditions particulières" htmlFor="ctr-conditions" className="md:col-span-2">
            <Textarea
              id="ctr-conditions"
              value={form.conditionsParticulieres}
              onChange={(event) => update("conditionsParticulieres", event.target.value)}
              placeholder="Clauses spécifiques, charges, travaux, modalités de paiement..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>
        </div>

        {!canSubmit && (
          <p className="flex items-center gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400" aria-hidden="true" />
            Un contrat nécessite un immeuble, un bien et un locataire. Créez-les d'abord dans leurs modules respectifs.
          </p>
        )}

      </form>
    </Modal>
  );
}